import React, {useRef} from 'react';
import {Box, Card, Chip, CircularProgress, styled, TablePagination} from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import ProfileStatus from './ProfileStatus';
import PropTypes from 'prop-types';
import useDynamicHeight from '../../../../utility/hooks/useDynamicHeight';
import Tooltip from '@mui/material/Tooltip';
import PROFILE_TYPE from '../../../../constants/profileTypes';
import orange from '@mui/material/colors/orange';
import red from '@mui/material/colors/red';

const ProfileCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'restricted'
})(({restricted}) => ({
    width: '170px',
    height: '100px',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    cursor: restricted ? 'default' : 'pointer',
    justifyContent: 'space-between',
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.06)',
    border: restricted ? '1px solid #ef9a9a' : '1px solid #e0e0e0',
    backgroundColor: restricted ? '#fafafa' : undefined,
    opacity: restricted ? 0.72 : 1,
}));

const ProfileInfoBox = styled(Box)({
    width: '100%',
    height: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
});

const ProfilesGrid = (
    {
        data,
        onProfileSelect,
        handlePaginationModelChange,
        totalItemCount,
        pageSize,
        pageNumber,
        isLoading,
        noRowsOverlay,
        readonly
    }) => {
    const parentRef = useRef(null);
    const paginationRef = useRef(null);
    const freeSpaceHeight = useDynamicHeight(parentRef);

    const handleChangePage = (event, newPage) => {
        handlePaginationModelChange(newPage, pageSize);
    };

    const handleChangeRowsPerPage = (event) => {
        handlePaginationModelChange(0, event.target.value);
    };

    const OverlayContainer = props => {
        let {children} = props;
        return <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
        >
            {children}
        </Box>;
    };

    OverlayContainer.propTypes = {children: PropTypes.any};

    const buildStudentName = (name) => name && name.length > 10 ? `${name.substring(0, 9)}...` : name;

    const buildConfirmIcon = (profile) => {
        if (profile.hasExistingContactAccount === false) {
            return <Tooltip title="None of the parent accounts have been created">
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{color: red[500]}}/>
            </Tooltip>;
        }

        return !profile.needsConfirmation || profile.needsConfirmation.length === 0
            ? <Tooltip title="Confirmed"><CheckOutlinedIcon color="success"/></Tooltip>
            : <Tooltip title={`${profile.needsConfirmation} sections needs confirmation`}>
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{color: orange[500]}}/>
            </Tooltip>
    }

    const getProfileListGrid = () => {
        return data?.map((profile) => {
            const isRestricted = Boolean(profile.isAccessRestricted);
            const isEnquiry = profile.personType === PROFILE_TYPE.ENQUIRY;
            const profileStatus = profile.personType === PROFILE_TYPE.APPLICATION || isEnquiry
                ? <ProfileStatus
                    status={isEnquiry ? 'Enquiry' : profile.status}
                    statusCategory={isEnquiry ? 'Enquiry' : profile.statusCategory}
                />
                : null;

            return (<Grid item key={profile.id} xs="auto">
                <Tooltip
                    title={isRestricted ? 'Access restricted — you cannot open this profile' : ''}
                    disableHoverListener={!isRestricted}
                >
                    <ProfileCard
                        restricted={isRestricted}
                        onClick={() => {
                            if (isRestricted || readonly) {
                                return;
                            }
                            onProfileSelect(profile);
                        }}
                        sx={readonly && !isRestricted ? {cursor: 'default'} : {}}
                    >
                        <Avatar
                            variant="square"
                            sx={{
                                width: 60,
                                height: 90,
                                borderRadius: 1,
                                filter: isRestricted ? 'grayscale(1)' : undefined
                            }}
                            src={profile.photo ? `data:image/jpeg;base64,${profile.photo}` : undefined}
                        />
                        <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'} height={'100%'}>
                            {
                                isRestricted
                                    ? (
                                        <Box display={'flex'} justifyContent={'end'}>
                                            <Chip
                                                size="small"
                                                icon={<LockOutlinedIcon sx={{fontSize: '14px !important'}}/>}
                                                label="Restricted"
                                                color="error"
                                                variant="outlined"
                                                sx={{
                                                    height: 20,
                                                    fontSize: 10,
                                                    '& .MuiChip-label': {px: 0.5},
                                                    '& .MuiChip-icon': {ml: 0.5}
                                                }}
                                            />
                                        </Box>
                                    )
                                    : (profile.personType === PROFILE_TYPE.APPLICATION ||
                                        profile.personType === PROFILE_TYPE.STUDENT) &&
                                    <Box display={'flex'} justifyContent={'end'}>
                                        {buildConfirmIcon(profile)}
                                    </Box>
                            }
                            <ProfileInfoBox>
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        width={'100px'}
                                        color={isRestricted ? 'text.secondary' : undefined}
                                    >
                                        {buildStudentName(profile.name?.trim())}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        width={'100px'}
                                        color={isRestricted ? 'text.secondary' : undefined}
                                    >
                                        {buildStudentName(profile.surname?.trim())}
                                    </Typography>
                                </Box>
                                <Box display={'flex'} justifyContent={'end'}>
                                    {!isRestricted && profileStatus}
                                </Box>
                            </ProfileInfoBox>
                        </Box>
                    </ProfileCard>
                </Tooltip>
            </Grid>);
        });
    };

    const NoProfilesView = () => <OverlayContainer>
        {
            noRowsOverlay?.component
                ? noRowsOverlay?.component
                : <Typography variant="h6">No data</Typography>
        }
    </OverlayContainer>;

    return (
        <Box
            ref={parentRef}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1
            }}
        >
            {
                isLoading
                    ? <OverlayContainer>
                        <CircularProgress size={70}/>
                    </OverlayContainer>
                    : <>
                        {
                            data?.length === 0
                                ? <NoProfilesView/>
                                : <>
                                    <Box sx={{
                                        paddingBottom: 2,
                                        maxHeight: `${freeSpaceHeight - 55 - 16}px`,
                                        overflowY: 'scroll'
                                    }}>
                                        <Grid
                                            container
                                            spacing={2}
                                        >
                                            {getProfileListGrid()}
                                        </Grid>
                                    </Box>
                                    <TablePagination
                                        ref={paginationRef}
                                        sx={{flexShrink: 0, height: 52}}
                                        rowsPerPageOptions={[20, 50, 100]}
                                        labelRowsPerPage=""
                                        component="div"
                                        count={totalItemCount}
                                        rowsPerPage={pageSize}
                                        page={pageNumber}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </>
                        }
                    </>
            }
        </Box>
    );
};

ProfilesGrid.propTypes = {
    data: PropTypes.array,
    onProfileSelect: PropTypes.func,
    handlePaginationModelChange: PropTypes.func,
    totalItemCount: PropTypes.number,
    pageSize: PropTypes.number,
    pageNumber: PropTypes.number,
    isLoading: PropTypes.bool,
    noRowsOverlay: PropTypes.object,
    readonly: PropTypes.bool
};

export default ProfilesGrid;
